import { computed, inject, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { PanelCategoriesEnum } from '@connect-services/client/components/project-settings/project-layers-settings/project-layers-settings.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { DataViewType } from '@connect-services/client/pages/management/data/deposit/deposit.component';
import { DetailPanelService } from '@connect-services/client/services/detail-panel.service';
import { CustomColumn, SortState } from '@connect-services/src/lib/components/table/table.component';
import {
    DepositControllerGetDepositsWithFolderAndStandardByProject200Response,
    DepositsService,
    DepositWithFolderAndStandard,
} from '@connect-services/src/sdk/api';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

type CustomDepositColumns = CustomColumn & {
    key: keyof DepositWithFolderAndStandard;
};

export type TableData = {
    deposits: DepositWithFolderAndStandard[] | null;
    isLoading: boolean;
    pagination: PaginationParameters;
    title: PanelCategoriesEnum;
    totalItems: number;
    type: string;
};

export class ProjectDepositsSettingsService {
    public readonly depositSections = computed<TableData[]>(() => [
        {
            deposits: this.organizationDataDeposits(),
            isLoading: this.isLoadingOrganizationData(),
            pagination: this.organizationDataPagination(),
            title: PanelCategoriesEnum.DEPOSIT,
            totalItems: this.organizationDataTotalItems(),
            type: DataViewType.ORGANIZATION_DATA,
        },
        {
            deposits: this.referenceDataDeposits(),
            isLoading: this.isLoadingReferenceData(),
            pagination: this.referenceDataPagination(),
            title: PanelCategoriesEnum.REFERENCE_DATA,
            totalItems: this.referenceDataTotalItems(),
            type: DataViewType.REFERENCE_DATA,
        },
    ]);
    public readonly tableColumns: CustomDepositColumns[] = [
        {
            key: 'depositFolder',
            label: 'data',
            sortable: true,
        },
        {
            key: 'version',
            label: 'version',
            sortable: true,
        },
        {
            key: 'layers',
            label: 'layer',
            sortable: false,
        },
        {
            key: 'standard',
            label: 'standard',
            sortable: true,
        },
        {
            key: 'updatedAt',
            label: 'importDate',
            sortable: true,
        },
    ];
    public readonly totalItems = computed<number>(
        () => this.organizationDataTotalItems() + this.referenceDataTotalItems(),
    );

    private readonly depositsService = inject(DepositsService);
    private readonly detailPanelService = inject(DetailPanelService);
    private readonly isLoadingOrganizationData = signal(false);
    private readonly isLoadingReferenceData = signal(false);
    private readonly organizationDataDeposits = signal<DepositWithFolderAndStandard[] | null>(null);
    private readonly organizationDataPagination = signal<PaginationParameters>(this.createDefaultDepositPagination());
    private readonly organizationDataTotalItems = signal(0);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly referenceDataDeposits = signal<DepositWithFolderAndStandard[] | null>(null);
    private readonly referenceDataPagination: WritableSignal<PaginationParameters> = signal(
        this.createDefaultDepositPagination(),
    );
    private readonly referenceDataTotalItems = signal<number>(0);
    private readonly router = inject(Router);
    private readonly searchValue = signal<string | undefined>(undefined);

    public async changePage(type: string, page: number): Promise<void> {
        if (type === DataViewType.ORGANIZATION_DATA) {
            this.organizationDataPagination.update((pagination: PaginationParameters) => ({
                ...pagination,
                page,
            }));

            await this.loadOrganizationDeposits();

            return;
        }

        this.referenceDataPagination.update((pagination: PaginationParameters) => ({
            ...pagination,
            page,
        }));

        await this.loadReferenceDeposits();
    }

    public async loadDeposits(): Promise<void> {
        await this.loadOrganizationDeposits();
        await this.loadReferenceDeposits();
    }

    public async searchDeposits(searchValue: string | undefined): Promise<void> {
        this.searchValue.set(searchValue);
        await this.loadDeposits();
    }

    public async sortDeposits(type: string, sort: SortState | undefined): Promise<void> {
        if (!sort) {
            return;
        }

        const paginationSignal: WritableSignal<PaginationParameters> =
            type === DataViewType.ORGANIZATION_DATA ? this.organizationDataPagination : this.referenceDataPagination;

        const loadDeposits =
            type === DataViewType.ORGANIZATION_DATA
                ? this.loadOrganizationDeposits.bind(this)
                : this.loadReferenceDeposits.bind(this);

        paginationSignal.update((pagination: PaginationParameters) => ({
            ...pagination,
            order: sort.order ?? OrderEnum.ASC,
            orderBy: sort.orderBy,
            page: pagination.page,
            take: 5,
        }));

        await loadDeposits();
    }

    public async toggleDepositDetailPanel(entityId: string): Promise<void> {
        const currentEntityId: string | null = this.detailPanelService.getDetailPanelUuidFromUrlByParameter(
            RoutePathEnum.DEPOSIT_DETAIL,
        );
        const panelParam: string | null = currentEntityId && currentEntityId === entityId ? null : entityId;
        const path: (string | undefined)[] = [
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()?.id,
            RoutePathEnum.SETTINGS,
        ];

        if (!panelParam) {
            await this.router.navigate([...path]);

            return;
        }

        await this.router.navigate([...path, { outlets: { detailPanel: [RoutePathEnum.DEPOSIT_DETAIL, panelParam] } }]);
    }

    private createDefaultDepositPagination(): PaginationParameters {
        return {
            ...defaultPaginationParameters,
            order: OrderEnum.ASC,
            orderBy: 'depositFolder',
            take: 5,
        };
    }

    private async loadOrganizationDeposits(): Promise<void> {
        this.isLoadingOrganizationData.set(true);

        try {
            const organizationDepositList: DepositControllerGetDepositsWithFolderAndStandardByProject200Response =
                await firstValueFrom(
                    this.depositsService.depositControllerGetDepositsWithFolderAndStandardByProject({
                        ...this.organizationDataPagination(),
                        projectId: this.projectBloc.value()!.id,
                        type: DataViewType.ORGANIZATION_DATA,
                        value: this.searchValue(),
                    }),
                );

            this.organizationDataDeposits.set(organizationDepositList.items);
            this.organizationDataTotalItems.set(organizationDepositList.totalItems);
        } catch {
            this.organizationDataDeposits.set([]);
            this.organizationDataTotalItems.set(0);
        } finally {
            this.isLoadingOrganizationData.set(false);
        }
    }

    private async loadReferenceDeposits(): Promise<void> {
        this.isLoadingReferenceData.set(true);
        try {
            const referenceDepositList: DepositControllerGetDepositsWithFolderAndStandardByProject200Response =
                await firstValueFrom(
                    this.depositsService.depositControllerGetDepositsWithFolderAndStandardByProject({
                        ...this.referenceDataPagination(),
                        projectId: this.projectBloc.value()!.id,
                        type: DataViewType.REFERENCE_DATA,
                        value: this.searchValue(),
                    }),
                );

            this.referenceDataDeposits.set(referenceDepositList.items);
            this.referenceDataTotalItems.set(referenceDepositList.totalItems);
        } catch {
            this.referenceDataDeposits.set([]);
            this.referenceDataTotalItems.set(0);
        } finally {
            this.isLoadingReferenceData.set(false);
        }
    }
}
